import { useState } from 'react';
import { SampleDataRibbon } from './SampleDataRibbon';
import { TrendingUp, DollarSign, Target, Calendar, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

export function MarketingMixModelPage() {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  // ROI by Channel Data
  const roiByChannel = [
    { channel: 'Influencer Marketing', roi: 2.3, spend: 125000, revenue: 287500, efficiency: 'High', color: '#ec4899' },
    { channel: 'Social Media', roi: 2.1, spend: 85000, revenue: 178500, efficiency: 'High', color: '#8b5cf6' },
    { channel: 'Paid Search', roi: 1.8, spend: 95000, revenue: 171000, efficiency: 'Medium', color: '#3b82f6' },
    { channel: 'TV', roi: 1.6, spend: 200000, revenue: 320000, efficiency: 'Medium', color: '#f59e0b' },
    { channel: 'Programmatic', roi: 1.4, spend: 75000, revenue: 105000, efficiency: 'Medium', color: '#10b981' },
    { channel: 'Email', roi: 2.5, spend: 35000, revenue: 87500, efficiency: 'High', color: '#06b6d4' },
  ];

  // Diminishing Returns Curves Data
  const diminishingReturnsData = {
    'Influencer Marketing': [
      { spend: 0, return: 0 },
      { spend: 25000, return: 55000 },
      { spend: 50000, return: 105000 },
      { spend: 75000, return: 150000 },
      { spend: 100000, return: 190000 },
      { spend: 125000, return: 225000 },
      { spend: 150000, return: 255000 },
      { spend: 175000, return: 280000 },
      { spend: 200000, return: 300000 },
    ],
    'Social Media': [
      { spend: 0, return: 0 },
      { spend: 20000, return: 40000 },
      { spend: 40000, return: 76000 },
      { spend: 60000, return: 108000 },
      { spend: 80000, return: 136000 },
      { spend: 100000, return: 160000 },
      { spend: 120000, return: 180000 },
      { spend: 140000, return: 196000 },
      { spend: 160000, return: 208000 },
    ],
    'Paid Search': [
      { spend: 0, return: 0 },
      { spend: 25000, return: 42000 },
      { spend: 50000, return: 82000 },
      { spend: 75000, return: 118000 },
      { spend: 100000, return: 150000 },
      { spend: 125000, return: 178000 },
      { spend: 150000, return: 202000 },
      { spend: 175000, return: 222000 },
      { spend: 200000, return: 238000 },
    ],
    'TV': [
      { spend: 0, return: 0 },
      { spend: 50000, return: 75000 },
      { spend: 100000, return: 145000 },
      { spend: 150000, return: 210000 },
      { spend: 200000, return: 270000 },
      { spend: 250000, return: 325000 },
      { spend: 300000, return: 375000 },
      { spend: 350000, return: 420000 },
      { spend: 400000, return: 460000 },
    ],
    'Programmatic': [
      { spend: 0, return: 0 },
      { spend: 20000, return: 26000 },
      { spend: 40000, return: 50000 },
      { spend: 60000, return: 72000 },
      { spend: 80000, return: 92000 },
      { spend: 100000, return: 110000 },
      { spend: 120000, return: 126000 },
      { spend: 140000, return: 140000 },
      { spend: 160000, return: 152000 },
    ],
    'Email': [
      { spend: 0, return: 0 },
      { spend: 10000, return: 24000 },
      { spend: 20000, return: 46000 },
      { spend: 30000, return: 66000 },
      { spend: 40000, return: 84000 },
      { spend: 50000, return: 100000 },
      { spend: 60000, return: 114000 },
      { spend: 70000, return: 126000 },
      { spend: 80000, return: 136000 },
    ],
  };

  // Optimized Channel Mix
  const optimizedMix = [
    { channel: 'TV', percentage: 32, budget: 195200, color: '#f59e0b' },
    { channel: 'Influencer Marketing', percentage: 25, budget: 152500, color: '#ec4899' },
    { channel: 'Social Media', percentage: 18, budget: 109800, color: '#8b5cf6' },
    { channel: 'Paid Search', percentage: 15, budget: 91500, color: '#3b82f6' },
    { channel: 'Programmatic', percentage: 6, budget: 36600, color: '#10b981' },
    { channel: 'Email', percentage: 4, budget: 24400, color: '#06b6d4' },
  ];

  // Budget Allocator - Current vs Optimal
  const budgetAllocator = [
    { channel: 'TikTok', current: 85, optimal: 62, currentBudget: 85000, optimalBudget: 62000 },
    { channel: 'Meta', current: 45, optimal: 55, currentBudget: 45000, optimalBudget: 55000 },
    { channel: 'Google', current: 50, optimal: 48, currentBudget: 50000, optimalBudget: 48000 },
    { channel: 'TV', current: 35, optimal: 42, currentBudget: 35000, optimalBudget: 42000 },
    { channel: 'OOH', current: 25, optimal: 30, currentBudget: 25000, optimalBudget: 30000 },
  ];

  // Weekly Spending Guidelines
  const weeklySpending = [
    { week: 'Week 1', influencer: 20000, social: 14000, search: 15000, tv: 32000, programmatic: 12000, email: 5600 },
    { week: 'Week 2', influencer: 22000, social: 15000, search: 16000, tv: 34000, programmatic: 13000, email: 5800 },
    { week: 'Week 3', influencer: 21000, social: 15500, search: 15500, tv: 33000, programmatic: 12500, email: 5700 },
    { week: 'Week 4', influencer: 23000, social: 16000, search: 17000, tv: 35000, programmatic: 13500, email: 6000 },
    { week: 'Week 5', influencer: 22500, social: 15800, search: 16500, tv: 34500, programmatic: 13200, email: 5900 },
    { week: 'Week 6', influencer: 24000, social: 16500, search: 17500, tv: 36000, programmatic: 14000, email: 6200 },
    { week: 'Week 7', influencer: 23500, social: 16200, search: 17200, tv: 35500, programmatic: 13800, email: 6100 },
    { week: 'Week 8', influencer: 25000, social: 17000, search: 18000, tv: 37000, programmatic: 14500, email: 6400 },
  ];

  // Model Diagnostics
  const modelDiagnostics = [
    { metric: 'R-Squared', value: '0.87', interpretation: 'Excellent model fit', status: 'good' },
    { metric: 'Adjusted R-Squared', value: '0.84', interpretation: 'Strong predictive power', status: 'good' },
    { metric: 'Durbin-Watson', value: '1.98', interpretation: 'No autocorrelation detected', status: 'good' },
    { metric: 'MAPE', value: '8.2%', interpretation: 'Low forecast error', status: 'good' },
    { metric: 'VIF (Max)', value: '3.2', interpretation: 'No multicollinearity issues', status: 'good' },
    { metric: 'Model Confidence', value: '92%', interpretation: 'High confidence in predictions', status: 'good' },
  ];

  const currentChannel = roiByChannel.find(c => c.channel === selectedChannel);
  const currentCurveData = selectedChannel ? diminishingReturnsData[selectedChannel as keyof typeof diminishingReturnsData] : null;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SampleDataRibbon />
      {/* Hero Banner */}
      <div className="bg-white border-b">
        <div 
          className="h-32 sm:h-48 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 relative"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/90 via-pink-600/90 to-orange-600/90"></div>
          <div className="relative h-full flex flex-col justify-end p-4 sm:p-8 max-w-[1800px] mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full w-fit mb-2 sm:mb-3">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              <span className="text-white text-xs sm:text-sm">Evolve</span>
            </div>
            <h1 className="text-white text-xl sm:text-4xl mb-1 sm:mb-2">MARKETING MIX MODEL (MMM)</h1>
            <p className="text-white/90 text-xs sm:text-sm">Optimize your marketing spend with data-driven insights</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Budget</p>
                  <p className="text-xl">$610K</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Avg. ROI</p>
                  <p className="text-xl">1.95x</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Active Channels</p>
                  <p className="text-xl">6</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Campaign Period</p>
                  <p className="text-xl">8 Weeks</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* ROI by Channel */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900">ROI by Channel</h3>
                  <p className="text-xs text-gray-500">Return on Investment per marketing channel</p>
                </div>
              </div>

              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roiByChannel} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 3]} tick={{ fontSize: 12 }} />
                    <YAxis dataKey="channel" type="category" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value: any) => [`${value}x`, 'ROI']}
                    />
                    <Bar dataKey="roi" radius={[0, 8, 8, 0]}>
                      {roiByChannel.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 space-y-2">
                {roiByChannel.map((channel) => (
                  <div key={channel.channel} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channel.color }}></div>
                      <span className="text-sm">{channel.channel}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">${(channel.spend / 1000).toFixed(0)}K spend</span>
                      <span className="text-sm">{channel.roi}x ROI</span>
                      <Badge className={`${channel.efficiency === 'High' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} border-0 text-xs`}>
                        {channel.efficiency}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Optimized Channel Mix */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900">Optimized Channel Mix</h3>
                  <p className="text-xs text-gray-500">Recommended budget allocation for maximum ROI</p>
                </div>
              </div>

              <div className="h-64 flex items-center justify-center mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={optimizedMix}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ channel, percentage }) => `${channel}: ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="percentage"
                    >
                      {optimizedMix.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value: any, name: any, props: any) => [`${value}% ($${(props.payload.budget / 1000).toFixed(1)}K)`, 'Allocation']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <span>Channel</span>
                  <div className="flex gap-8">
                    <span>Allocation</span>
                    <span>Budget</span>
                  </div>
                </div>
                {optimizedMix.map((channel) => (
                  <div key={channel.channel} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: channel.color }}></div>
                      <span className="text-sm">{channel.channel}</span>
                    </div>
                    <div className="flex items-center gap-8">
                      <span className="text-sm">{channel.percentage}%</span>
                      <span className="text-sm">${(channel.budget / 1000).toFixed(1)}K</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Budget Allocator - Current vs Optimal */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-gray-900 mb-1">Predicted Revenue</h3>
              <div className="flex items-center gap-6 mb-6">
                <div>
                  <p className="text-xs text-gray-500">Incremental revenue</p>
                  <p className="text-2xl">$500k <span className="text-sm text-green-600">(+17%)</span></p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Predicted ROI</p>
                  <p className="text-2xl">3.2 <span className="text-sm text-green-600">(+38%)</span></p>
                </div>
              </div>

              <h4 className="text-sm mb-3">Budget allocator</h4>
              <div className="flex items-center gap-3 mb-3 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>Current</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-pink-500 rounded"></div>
                  <span>Optimal</span>
                </div>
              </div>

              <div className="space-y-3">
                {budgetAllocator.map((item) => (
                  <div key={item.channel}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{item.channel}</span>
                    </div>
                    <div className="relative h-8 bg-gray-100 rounded">
                      {/* Current budget bar */}
                      <div 
                        className="absolute top-1 left-0 h-3 bg-blue-500 rounded"
                        style={{ width: `${item.current}%` }}
                      ></div>
                      {/* Optimal budget bar */}
                      <div 
                        className="absolute bottom-1 left-0 h-3 bg-pink-500 rounded"
                        style={{ width: `${item.optimal}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                      <span>0k</span>
                      <span>25k</span>
                      <span>50k</span>
                      <span>75k</span>
                      <span>100k</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Diminishing Returns Curves */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900">Diminishing Returns Curves</h3>
                  <p className="text-xs text-gray-500">Relationship between spend and returns for each channel</p>
                </div>
              </div>

              {/* Channel Selection */}
              <div className="flex flex-wrap gap-2 mb-4">
                {roiByChannel.map((channel) => (
                  <Button
                    key={channel.channel}
                    variant={selectedChannel === channel.channel ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedChannel(channel.channel)}
                    className={selectedChannel === channel.channel ? 'text-white' : ''}
                    style={selectedChannel === channel.channel ? { backgroundColor: channel.color } : {}}
                  >
                    {channel.channel}
                  </Button>
                ))}
              </div>

              {currentChannel && currentCurveData ? (
                <>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={currentCurveData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id={`colorReturn-${selectedChannel}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={currentChannel.color} stopOpacity={0.8}/>
                            <stop offset="95%" stopColor={currentChannel.color} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="spend" 
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                          label={{ value: 'Marketing Spend', position: 'insideBottom', offset: -5, fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                          label={{ value: 'Incremental Return', angle: -90, position: 'insideLeft', fontSize: 12 }}
                        />
                        <Tooltip 
                          contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          formatter={(value: any) => [`$${(value / 1000).toFixed(1)}K`, 'Return']}
                          labelFormatter={(value: any) => `Spend: $${(value / 1000).toFixed(1)}K`}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="return" 
                          stroke={currentChannel.color} 
                          strokeWidth={3}
                          fillOpacity={1} 
                          fill={`url(#colorReturn-${selectedChannel})`} 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Info className="w-4 h-4 text-purple-600" />
                        <p className="text-xs text-gray-600">Current Spend</p>
                      </div>
                      <p className="text-xl">${(currentChannel.spend / 1000).toFixed(0)}K</p>
                      <p className="text-xs text-gray-500 mt-1">Generating ${(currentChannel.revenue / 1000).toFixed(0)}K in returns</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <p className="text-xs text-gray-600">Current ROI</p>
                      </div>
                      <p className="text-xl">{currentChannel.roi}x</p>
                      <p className="text-xs text-gray-500 mt-1">{currentChannel.efficiency} efficiency rating</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <p className="text-xs text-gray-600">Saturation Point</p>
                      </div>
                      <p className="text-xl">~${(currentChannel.spend * 1.6 / 1000).toFixed(0)}K</p>
                      <p className="text-xs text-gray-500 mt-1">Consider diversifying beyond this</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Select a channel to view its response curve</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weekly Spending Guidelines */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900">Weekly Spending Guidelines</h3>
                  <p className="text-xs text-gray-500">Recommended spend distribution across campaign period</p>
                </div>
              </div>

              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklySpending} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value: any) => [`$${(value / 1000).toFixed(1)}K`, '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="tv" stroke="#f59e0b" strokeWidth={2} name="TV" />
                    <Line type="monotone" dataKey="influencer" stroke="#ec4899" strokeWidth={2} name="Influencer" />
                    <Line type="monotone" dataKey="social" stroke="#8b5cf6" strokeWidth={2} name="Social" />
                    <Line type="monotone" dataKey="search" stroke="#3b82f6" strokeWidth={2} name="Search" />
                    <Line type="monotone" dataKey="programmatic" stroke="#10b981" strokeWidth={2} name="Programmatic" />
                    <Line type="monotone" dataKey="email" stroke="#06b6d4" strokeWidth={2} name="Email" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 text-xs text-gray-500">Week</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">TV</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">Influencer</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">Social</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">Search</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">Programmatic</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">Email</th>
                      <th className="text-right py-3 px-3 text-xs text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklySpending.map((week) => {
                      const total = week.tv + week.influencer + week.social + week.search + week.programmatic + week.email;
                      return (
                        <tr key={week.week} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-3">{week.week}</td>
                          <td className="text-right py-3 px-3">${(week.tv / 1000).toFixed(1)}K</td>
                          <td className="text-right py-3 px-3">${(week.influencer / 1000).toFixed(1)}K</td>
                          <td className="text-right py-3 px-3">${(week.social / 1000).toFixed(1)}K</td>
                          <td className="text-right py-3 px-3">${(week.search / 1000).toFixed(1)}K</td>
                          <td className="text-right py-3 px-3">${(week.programmatic / 1000).toFixed(1)}K</td>
                          <td className="text-right py-3 px-3">${(week.email / 1000).toFixed(1)}K</td>
                          <td className="text-right py-3 px-3">${(total / 1000).toFixed(1)}K</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Model Diagnostics */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900">Model Diagnostics</h3>
                  <p className="text-xs text-gray-500">Statistical measures of model reliability and fit</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modelDiagnostics.map((diagnostic) => (
                  <div key={diagnostic.metric} className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">{diagnostic.metric}</p>
                        <p className="text-2xl">{diagnostic.value}</p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-xs text-gray-500">{diagnostic.interpretation}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm mb-1">Model Quality Assessment</p>
                    <p className="text-xs text-gray-600">
                      This Marketing Mix Model demonstrates excellent statistical reliability with an R-squared of 0.87, 
                      indicating that 87% of variance in sales is explained by marketing activities. The model shows no 
                      autocorrelation (Durbin-Watson: 1.98) and maintains low forecast error (MAPE: 8.2%). All diagnostics 
                      indicate this model is suitable for strategic budget allocation decisions.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}